import React from 'react';
import styled, { useTheme, DefaultTheme } from 'styled-components/macro';
import IconWrapper from '../../icons/IconWrapper';
import { IconX } from '../../icons';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils/style';
import { hexToRgba } from '../../utils/colors';

type PILL_TYPE = 'dark' | 'primary' | 'light' | 'outline';

function getPillStyle(type: PILL_TYPE, theme: DefaultTheme) {
  return {
    dark: {
      backgroundColor: theme.colors.secondary40w,
      color: theme.colors.white,
      border: `1px solid ${theme.colors.secondary40w}`,
    },
    light: {
      backgroundColor: theme.colors.secondary90w,
      color: theme.colors.black,
      border: `1px solid ${theme.colors.secondary90w}`,
    },
    primary: {
      backgroundColor: theme.colors.primary60w,
      color: theme.colors.black,
      border: `1px solid ${theme.colors.primary60w}`,
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.black,
      border: `1px solid ${hexToRgba(theme.colors.black, 0.7)}`,
    },
  }[type];
}

function getPillSize(size: 'sm' | 'base' | 'lg') {
  return {
    sm: {
      padding: `0 ${SPACINGS[2]}`,
    },
    base: {
      padding: `0 ${SPACINGS[3]}`,
    },
    lg: {
      padding: `0 ${SPACINGS[4]}`,
    },
  }[size];
}

type PillStyledProps = {
  type: PILL_TYPE;
  size: 'sm' | 'base' | 'lg';
  backgroundColor: string;
  color: string;
  customBackgroundColor?: string;
  customColor?: string;
  customBorder?: string;
  border: string;
  padding: string;
};
export const PillStyled = styled.div<PillStyledProps>`
  display: inline-flex;
  background-color: ${(p) => p.customBackgroundColor ?? p.backgroundColor};
  color: ${(p) => p.customColor ?? hexToRgba(p.color, 0.7)};
  border: ${(p) =>
    p.customBorder
      ? p.customBorder
      : p.customBackgroundColor
      ? `1px solid ${p.customBackgroundColor}`
      : p.border};
  border-radius: 8px;
  align-items: center;
  padding: ${(p) => p.padding};
  white-space: nowrap;
`;

type PillProps = {
  children: React.ReactNode;
  size?: 'sm' | 'base' | 'lg';
  type?: PILL_TYPE;
  color?: string;
  backgroundColor?: string;
  border?: string;
  onRemove?: () => void;
  style?: React.CSSProperties;
  testid?: string;
};

function Pill(props: PillProps) {
  const {
    onRemove,
    type = 'dark',
    size = 'base',
    style,
    children,
    color,
    backgroundColor,
    border,
    testid,
  } = props;
  const theme = useTheme();

  const pillStyle = getPillStyle(type, theme);
  const pillSize = getPillSize(size);

  return (
    <PillStyled
      size={size}
      type={type}
      style={style}
      padding={pillSize.padding}
      {...pillStyle}
      customColor={color}
      customBorder={border}
      customBackgroundColor={backgroundColor}
      data-testid={testid}
    >
      <Text margin="none" size={size} style={{ color: 'unset' }}>
        {children}
      </Text>
      {onRemove && (
        <IconWrapper
          style={{
            cursor: 'pointer',
            marginLeft: SPACINGS[2],
            width: SPACINGS[4],
            height: SPACINGS[4],
          }}
          onClick={onRemove}
          aria-label="Remove"
        >
          <IconX stroke={hexToRgba(pillStyle.color, 0.7)} />
        </IconWrapper>
      )}
    </PillStyled>
  );
}

export default React.memo(Pill);
